<template>
  <div class="tracking-page">
    <Head title="Tracking Management" />

    <!-- Header -->
    <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.14em] text-brand-500">
          Operations
        </p>
        <h1 class="mt-1 text-3xl font-bold tracking-tight text-brand-900">
          Tracking Management
        </h1>
        <p class="mt-1.5 text-sm text-gray-500">
          Filter and review user tracking steps across channels and campaigns.
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <button
          type="button"
          class="inline-flex items-center rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 shadow-sm transition hover:bg-gray-50"
          @click="reset"
        >
          Reset
        </button>
        <button
          type="button"
          class="inline-flex items-center rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700"
          @click="openDownloadModal"
        >
          Download Excel
        </button>
      </div>
    </div>

    <!-- Summary strip -->
    <div class="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div class="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Tracking results</p>
        <p class="mt-1 text-xl font-bold text-brand-900">
          {{ formatNumber(organizations.total || 0) }}
        </p>
        <p class="mt-1 text-[11px] text-gray-400">Funnel sessions (not submissions)</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Page</p>
        <p class="mt-1 text-xl font-bold text-brand-900">
          {{ organizations.current_page || 1 }}
          <span class="text-sm font-medium text-gray-400">/ {{ organizations.last_page || 1 }}</span>
        </p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Visible columns</p>
        <p class="mt-1 text-xl font-bold text-brand-900">
          {{ (columns && columns.length) || 0 }}
        </p>
      </div>
    </div>

    <!-- Filters -->
    <div class="mb-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div class="mb-4 flex items-center justify-between">
        <div>
          <h2 class="text-sm font-bold text-brand-900">Filters</h2>
          <p class="text-xs text-gray-500">Narrow results by date, channel, step, source, or keyword</p>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Start date</label>
          <input
            v-model="form.datestart"
            type="date"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          />
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">End date</label>
          <input
            v-model="form.dateend"
            type="date"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          />
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Source name</label>
          <input
            v-model="form.sourcename"
            type="text"
            placeholder="utm source..."
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          />
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Channel</label>
          <select
            v-model="form.channel"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            <option value="">All channels</option>
            <option
              v-for="option in options"
              :key="option.value"
              :value="option.value"
            >
              {{ option.text }}
            </option>
          </select>
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Step</label>
          <select
            v-model="form.step"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            <option value="">All steps</option>
            <option value="1">Step 1</option>
            <option value="2">Step 2</option>
            <option value="3">Step 3</option>
            <option value="4">Step 4</option>
            <option value="5">Step 5</option>
          </select>
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Search</label>
          <input
            v-model="form.search"
            type="text"
            placeholder="Name, phone, token..."
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
            @keyup.enter="applyFilters"
          />
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Per page</label>
          <select
            v-model="form.paging"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            <option :value="5">5</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
      </div>

      <div class="mt-5 flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p class="text-xs text-gray-500">
          Set your filters, then click Apply to refresh the list.
        </p>
        <div class="flex flex-wrap items-center gap-2 sm:justify-end">
          <button
            type="button"
            class="inline-flex items-center rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 shadow-sm transition hover:bg-gray-50"
            @click="reset"
          >
            Reset
          </button>
          <button
            type="button"
            class="inline-flex items-center rounded-xl bg-brand-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700"
            @click="applyFilters"
          >
            Apply filters
          </button>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div class="flex items-center justify-between border-b border-gray-100 px-5 py-3">
        <div>
          <h2 class="text-sm font-bold text-brand-900">Tracking records</h2>
          <p class="text-xs text-gray-500">
            Showing
            <span class="font-semibold text-brand-700">{{ organizations.data.length }}</span>
            of
            <span class="font-semibold text-brand-700">{{ formatNumber(organizations.total || 0) }}</span>
          </p>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full">
          <thead>
            <tr class="border-b border-gray-100 bg-brand-50/60 text-left">
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">
                No
              </th>
              <th
                v-for="column in columns"
                :key="column.key"
                class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600"
                :style="column.format === 'step' ? { minWidth: '14rem' } : undefined"
              >
                {{ column.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(record, index) in organizations.data"
              :key="index"
              class="border-b border-gray-50 transition-colors hover:bg-brand-50/40"
            >
              <td class="whitespace-nowrap px-4 py-3.5 text-sm font-semibold text-gray-500">
                {{ calculateIncrement(index) }}
              </td>
              <td
                v-for="column in columns"
                :key="column.key"
                class="px-4 py-3.5 align-top text-sm text-gray-700"
                :class="cellClass(column)"
                :style="column.format === 'step' ? { minWidth: '14rem' } : undefined"
              >
                <template v-if="column.format === 'step'">
                  <div class="space-y-1.5">
                    <div class="flex items-center justify-between text-[11px] font-semibold text-gray-500">
                      <span>Progress</span>
                      <span>{{ stepCompleted(record[column.key]) }}/5</span>
                    </div>
                    <div class="flex gap-1">
                      <span
                        v-for="n in 5"
                        :key="n"
                        class="h-1.5 flex-1 rounded-full"
                        :class="n <= stepCompleted(record[column.key]) ? 'bg-teal-500' : 'bg-gray-200'"
                      />
                    </div>
                    <p class="whitespace-pre-line text-xs leading-relaxed text-gray-600">
                      {{ formatStepDisplay(record[column.key]) }}
                    </p>
                  </div>
                </template>
                <template v-else-if="column.format === 'device'">
                  <span
                    class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold"
                    :class="deviceBadgeClass(record[column.key])"
                  >
                    {{ cellValue(column, record) }}
                  </span>
                </template>
                <template v-else-if="column.format === 'currency'">
                  <span class="font-semibold text-brand-800">
                    {{ cellValue(column, record) }}
                  </span>
                </template>
                <template v-else>
                  {{ cellValue(column, record) }}
                </template>
              </td>
            </tr>

            <tr v-if="!organizations.data.length">
              <td :colspan="(columns && columns.length ? columns.length : 0) + 1" class="px-4 py-16 text-center">
                <div class="mx-auto max-w-sm">
                  <p class="text-base font-bold text-brand-900">No records found</p>
                  <p class="mt-1 text-sm text-gray-500">
                    Try adjusting the date range, channel, or search keyword.
                  </p>
                  <button
                    type="button"
                    class="mt-4 rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
                    @click="reset"
                  >
                    Clear filters
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="border-t border-gray-100 bg-gray-50 px-5 py-3">
        <pagination class="mt-0" :links="organizations.links || []" preserve-scroll />
      </div>
    </div>

    <!-- Download Excel modal -->
    <div
      v-if="showDownloadModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      @click.self="closeDownloadModal"
    >
      <div class="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        <div class="border-b border-gray-100 px-5 py-4">
          <h3 class="text-base font-bold text-brand-900">Download Excel</h3>
          <p class="mt-1 text-sm text-gray-500">
            Choose the date range for the export. Channel, source, and search filters from the page will still apply.
          </p>
        </div>

        <div class="space-y-4 px-5 py-4">
          <div>
            <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Quick range</p>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="preset in downloadPresets"
                :key="preset.key"
                type="button"
                class="rounded-full border px-3 py-1.5 text-xs font-semibold transition"
                :class="downloadForm.preset === preset.key
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'"
                @click="applyDownloadPreset(preset.key)"
              >
                {{ preset.label }}
              </button>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label class="mb-1.5 block text-xs font-semibold text-gray-600">Start date</label>
              <input
                v-model="downloadForm.datestart"
                type="date"
                class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
                @change="downloadForm.preset = 'custom'"
              />
            </div>
            <div>
              <label class="mb-1.5 block text-xs font-semibold text-gray-600">End date</label>
              <input
                v-model="downloadForm.dateend"
                type="date"
                class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
                @change="downloadForm.preset = 'custom'"
              />
            </div>
          </div>

          <label class="flex items-start gap-2 rounded-xl border border-gray-100 bg-gray-50 px-3 py-3 text-sm text-gray-600">
            <input
              v-model="downloadForm.useCurrentFilters"
              type="checkbox"
              class="mt-0.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            <span>
              Keep current page filters (channel, source name, search).
            </span>
          </label>

          <p v-if="downloadError" class="text-sm font-medium text-red-600">
            {{ downloadError }}
          </p>
        </div>

        <div class="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-4">
          <button
            type="button"
            class="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
            @click="closeDownloadModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700"
            @click="confirmDownloadExcel"
          >
            Download
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import SearchFilter from '@/Shared/SearchFilter'
import moment from 'moment'

export default {
  components: {
    Head,
    Icon,
    Link,
    Pagination,
    SearchFilter,
  },
  layout: Layout,

  props: {
    filters: Object,
    optionsData: Array,
    organizations: Object,
    // Columns the logged in user is allowed to see, resolved server side from
    // their role and the selection saved on their user record.
    columns: Array,
  },
  data() {
    return {
      form: {
        search: this.filters.search,
        trashed: this.filters.trashed,
        selected: this.filters.pilihan,
        channel: this.filters.channel,
        step: this.filters.step || '',
        paging: this.filters.paging || 25,
        datestart: this.filters.datestart,
        dateend: this.filters.dateend,
        sourcename: this.filters.sourcename,
        page: 1,
      },
      value: null,
      showDownloadModal: false,
      downloadError: '',
      downloadForm: {
        preset: 'today',
        datestart: '',
        dateend: '',
        useCurrentFilters: true,
      },
      downloadPresets: [
        { key: 'today', label: 'Today' },
        { key: '7days', label: 'Last 7 days' },
        { key: 'month', label: 'This month' },
        { key: 'current', label: 'Use page dates' },
        { key: 'custom', label: 'Custom' },
      ],
    }
  },
  computed: {
    options() {
      if (Array.isArray(this.optionsData)) {
        return this.optionsData
          .map((option) => {
            if (option && typeof option === 'object' && 'agtCd' in option && 'name' in option) {
              return {
                value: option.agtCd,
                text: option.name,
              }
            }
            return null
          })
          .filter((option) => option !== null)
      }

      return []
    },
  },
  methods: {
    applyFilters() {
      this.$inertia.get('/custmanagement', pickBy(this.form), { preserveState: true })
    },
    formatNumber(value) {
      if (value == null) return '0'
      return Number(value).toLocaleString('id-ID')
    },
    formatCurrency(amount) {
      const originalText = amount.toLocaleString('id-ID', { style: 'currency', currency: 'IDR' })
      return originalText.replace(',00', '')
    },
    stepCompleted(step) {
      return Number(step) || 0
    },
    formatStepDisplay(step) {
      const totalSteps = 5
      const completed = this.stepCompleted(step)
      const lines = []
      for (let i = 1; i <= totalSteps; i++) {
        const status = i <= completed ? 'Complete' : 'Pending'
        lines.push(`Step ${i}: ${status}`)
      }
      return lines.join('\n')
    },
    formatDate(date) {
      return moment.utc(date).format('DD/MM/YY, H:mm:ss')
    },
    cellClass(column) {
      if (column.format === 'step') return ''
      return 'whitespace-nowrap'
    },
    deviceBadgeClass(value) {
      if (value === '001') return 'bg-teal-50 text-teal-700'
      if (value === '002') return 'bg-amber-50 text-amber-700'
      return 'bg-gray-100 text-gray-500'
    },
    cellValue(column, record) {
      const value = record[column.key]

      switch (column.format) {
        case 'datetime':
          return value ? this.formatDate(value) : '-'
        case 'currency':
          return value ? this.formatCurrency(parseFloat(value)) : '-'
        case 'step':
          return this.formatStepDisplay(value)
        case 'device':
          if (value === '001') return 'Mobile'
          if (value === '002') return 'Web Desktop'
          return '-'
        case 'tenor':
          return value ? `${value} Bulan` : '-'
        case 'organik':
          return value || 'Organik'
        default:
          return value === null || value === undefined || value === '' ? '-' : value
      }
    },
    openDownloadModal() {
      this.downloadError = ''
      this.downloadForm = {
        preset: this.form.datestart || this.form.dateend ? 'current' : 'today',
        datestart: this.form.datestart || '',
        dateend: this.form.dateend || '',
        useCurrentFilters: true,
      }
      if (!this.form.datestart && !this.form.dateend) {
        this.applyDownloadPreset('today')
      }
      this.showDownloadModal = true
    },
    closeDownloadModal() {
      this.showDownloadModal = false
      this.downloadError = ''
    },
    applyDownloadPreset(preset) {
      this.downloadForm.preset = preset
      const today = moment()

      if (preset === 'today') {
        this.downloadForm.datestart = today.format('YYYY-MM-DD')
        this.downloadForm.dateend = today.format('YYYY-MM-DD')
        return
      }

      if (preset === '7days') {
        this.downloadForm.datestart = today.clone().subtract(6, 'days').format('YYYY-MM-DD')
        this.downloadForm.dateend = today.format('YYYY-MM-DD')
        return
      }

      if (preset === 'month') {
        this.downloadForm.datestart = today.clone().startOf('month').format('YYYY-MM-DD')
        this.downloadForm.dateend = today.format('YYYY-MM-DD')
        return
      }

      if (preset === 'current') {
        this.downloadForm.datestart = this.form.datestart || ''
        this.downloadForm.dateend = this.form.dateend || ''
      }
    },
    confirmDownloadExcel() {
      this.downloadError = ''

      if (!this.downloadForm.datestart || !this.downloadForm.dateend) {
        this.downloadError = 'Please select both start and end dates.'
        return
      }

      if (this.downloadForm.datestart > this.downloadForm.dateend) {
        this.downloadError = 'Start date must be before or equal to end date.'
        return
      }

      const params = {
        datestart: this.downloadForm.datestart,
        dateend: this.downloadForm.dateend,
      }

      if (this.downloadForm.useCurrentFilters) {
        if (this.form.channel) params.channel = this.form.channel
        if (this.form.step) params.step = this.form.step
        if (this.form.sourcename) params.sourcename = this.form.sourcename
        if (this.form.search) params.search = this.form.search
      }

      const queryParams = new URLSearchParams(pickBy(params)).toString()
      window.location = `/download-excel?${queryParams}`
      this.closeDownloadModal()
    },
    calculateIncrement(index) {
      const currentPage = this.organizations.current_page
      const pageSize = this.organizations.per_page
      return (currentPage - 1) * pageSize + index + 1
    },
    reset() {
      this.form = {
        search: null,
        trashed: null,
        selected: null,
        channel: null,
        step: '',
        paging: 25,
        datestart: null,
        dateend: null,
        sourcename: null,
        page: 1,
      }
      this.applyFilters()
    },
  },
}
</script>
